import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faExternalLinkAlt,
  faArrowRight,
  faPause,
  faPlay,
} from "@fortawesome/free-solid-svg-icons"; // 导入需要的图标
const Carousel = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const loadCarouselData = async () => {
      try {
        const response = await fetch("/json/carousel.json");
        if (!response.ok) {
          throw new Error("Failed to load carousel data");
        }
        const data = await response.json();

        if (!Array.isArray(data) || data.length === 0) {
          throw new Error("Invalid carousel data format");
        }

        setSlides(data);
        const activeIndex = data.findIndex((slide) => slide.active);
        if (activeIndex !== -1) {
          setCurrentSlide(activeIndex);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading carousel data:", error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    loadCarouselData();
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // 自动播放功能
  useEffect(() => {
    if (!isPlaying || slides.length <= 1) return;

    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isPlaying, slides.length, nextSlide]);

  // 触摸滑动支持
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // 键盘导航支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          prevSlide();
          break;
        case "ArrowRight":
          nextSlide();
          break;
        case " ":
          e.preventDefault();
          setIsPlaying((prev) => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  if (isLoading) {
    return (
      <div className="carousel-container">
        <div className="carousel-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading carousel...</span>
          </div>
          <p className="mt-3 text-muted">Loading latest updates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="carousel-container">
        <div className="carousel-error">
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">Unable to load carousel</h4>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return null;
  }

  const currentSlideData = slides[currentSlide];
  const isExternalLink = currentSlideData?.link?.startsWith("http");

  return (
    <div
      className="carousel-container"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsPlaying(false)}
      onMouseLeave={() => setIsPlaying(true)}
      style={{
        position: "relative",
        width: "100%",
        height: "48vh",
        overflow: "hidden",
        borderRadius: "15px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
        marginBottom: "20px",
      }}
    >
      {/* 背景图片 */}
      <div
        className="carousel-background"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: ` url(${currentSlideData.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transition: "all 0.8s ease-in-out",
        }}
      />
      <div
        className="slide-number"
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          background: "rgba(0,0,0,0.5)",
          padding: "5px 10px",
          borderRadius: "15px",
          fontSize: "0.9rem",
          color: "#fff",
        }}
      >
        {currentSlide + 1} / {slides.length}
      </div>

      {/* 内容区域 */}
      <div
        className="carousel-content"
        style={{
          position: "absolute",
          zIndex: 2,
          height: "40%",
          width: "100%",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          backgroundImage: `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.6))`,
        }}
      >
        <div
          className="slide-content fade-in-up"
          style={{
            animation: "fadeInUp 0.8s ease-out",
          }}
        >
          <a
            style={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              color: "#fff",
              width: "100%",
              display: "block",
              textDecoration: "none",
            }}
            className="gradient-text-white pb-2"
            href={currentSlideData.link}
            target="_blank"
          >
            {currentSlideData.title}
          </a>

          <div
            className="carousel-actions"
            style={{
              display: "flex",
              gap: "15px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!isMobile && isExternalLink && (
              <a
                href={currentSlideData.link}
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: "12px 30px",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  textDecoration: "none",
                  borderRadius: "25px",
                  background:
                    "linear-gradient(135deg, #000000 0%, #003366 100%)",
                  border: "none",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                }}
              >
                <i
                  className="fas fa-external-link-alt"
                  style={{ marginRight: "8px" }}
                ></i>
                {currentSlideData.button_text || "Learn More"}
              </a>
            )}
          </div>

          {/* 指示器 */}
          {slides.length > 1 && (
            <div
              className="carousel-indicators m-2"
              style={{
                position: "absolute",
                display: "flex",
                gap: "10px",
                zIndex: 3,
                paddingBottom: "10px",
              }}
            >
              {slides.map((slide, index) => (
                <button
                  key={slide.id || index}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}: ${slide.title}`}
                  title={slide.title}
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    border: "2px solid white",
                    background:
                      index === currentSlide ? "white" : "transparent",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <span
                    className="indicator-progress"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background:
                        index === currentSlide && isPlaying
                          ? "rgba(255,255,255,0.5)"
                          : "transparent",
                      borderRadius: "50%",
                      animation:
                        index === currentSlide && isPlaying
                          ? "pulse 5s infinite"
                          : "none",
                    }}
                  ></span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 导航箭头 */}
      {slides.length > 1 && (
        <>
          <button
            className="carousel-nav prev"
            onClick={prevSlide}
            aria-label="Previous slide"
            title="Previous slide (←)"
            style={{
              position: "absolute",
              left: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              fontSize: "20px",
              cursor: "pointer",
              zIndex: 3,
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,255,255,0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.2)";
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <button
            className="carousel-nav next"
            onClick={nextSlide}
            aria-label="Next slide"
            title="Next slide (→)"
            style={{
              position: "absolute",
              right: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              fontSize: "20px",
              cursor: "pointer",
              zIndex: 3,
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,255,255,0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.2)";
            }}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}

      {/* 进度条 */}
      {isPlaying && slides.length > 1 && (
        <div
          className="carousel-progress"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "3px",
            background: "rgba(255,255,255,0.3)",
            zIndex: 3,
          }}
        >
          <div
            className="carousel-progress-bar"
            style={{
              height: "100%",
              background: "linear-gradient(90deg, #667eea, #764ba2)",
              width: "0%",
              animation: isPlaying ? "progressBar 5s linear infinite" : "none",
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default Carousel;
